import type { ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import { Button } from "../Button/Button";
import { cn } from "../lib/cn";

export const COPY_FIELD_COPIED_LABEL = "Copied";
export const COPY_FIELD_RESET_MS = 2000;

export type typeCopyFieldProps = {
  /** The field's own label, e.g. "Invite code". */
  label: string;
  /** The value shown and copied. */
  value: string;
  /** One supporting sentence under the value. */
  hint?: string | undefined;
  /** The copy button's resting accessible label, e.g. "Copy invite code". */
  copyLabel: string;
  className?: string | undefined;
} & { "data-testid"?: string | undefined };

/**
 * A labeled value with its own copy control and its own inline `Copied`
 * confirmation. This product has no toasts and no snackbars, so a copy
 * outcome is announced inline, per-field, never as a shared indicator.
 */
export const CopyField = (props: typeCopyFieldProps): ReactNode => {
  const [isCopied, setIsCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCopy = useCallback(() => {
    // The DOM lib types `navigator.clipboard` as always present, but older
    // browsers and this kit's own test environment (before a stub is
    // installed) genuinely omit it. The guard is a real runtime check.
    // oxlint-disable-next-line typescript/no-unnecessary-condition -- plan-mandated guard, see comment above
    if (typeof navigator === "undefined" || !navigator.clipboard) return;
    void navigator.clipboard.writeText(props.value).then(
      () => {
        setIsCopied(true);
      },
      () => {
        // A rejected clipboard write is not an API error. Never raise the
        // error modal and never show a toast for it.
      },
    );
  }, [props.value]);

  useEffect(() => {
    if (!isCopied) return;
    timerRef.current = setTimeout(() => {
      setIsCopied(false);
    }, COPY_FIELD_RESET_MS);
    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    };
  }, [isCopied]);

  return (
    <div className={props.className} data-testid={props["data-testid"]}>
      <span className="text-caption text-ink-secondary">{props.label}</span>
      <div className="flex flex-wrap items-center gap-3">
        <span className={cn("text-body-strong text-ink", "break-all")}>
          {props.value}
        </span>
        <Button
          variant="secondary"
          size="sm"
          type="button"
          onClick={handleCopy}
        >
          {isCopied ? COPY_FIELD_COPIED_LABEL : props.copyLabel}
        </Button>
      </div>
      {props.hint ? (
        <p className="text-caption text-ink-tertiary">{props.hint}</p>
      ) : null}
      <span aria-live="polite" className="sr-only">
        {isCopied ? COPY_FIELD_COPIED_LABEL : ""}
      </span>
    </div>
  );
};
